import React, { useCallback, useEffect, useMemo } from "react"
import { useRouter } from "next/router"

import { LogInRequired } from "@/app-shell/layouts/LogInRequired"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Card,
} from "@coval/ui"
import { Button } from "@heroui/react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { usePageHeaderController } from "@/contexts/PageHeaderContext"
import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import { useQueryParams } from "@/hooks/useQueryParams"

import {
  CalendarDateRangeButton,
  DashboardActions,
  EmptyDashboardState,
} from "."
import { useDashboardDatePreferences } from "../hooks/useDashboardDatePreferences"
import { usePointSelection } from "../hooks/usePointSelection"
import { useUnifiedMetricDrillDownInsights } from "../hooks/useUnifiedMetricDrillDownInsights"
import { useUnifiedMetricDrillDownNavigation } from "../hooks/useUnifiedMetricDrillDownNavigation"
import { DashboardDrilldownInsightsPanel } from "../llm-generation"
import { formatBinaryValue } from "../utils/binaryFormatter"
import { getDrillDownBreadcrumbLabel } from "../utils/drillDownBreadcrumbs"
import { buildDrillDownConversationPath } from "../utils/drillDownNavigation"
import { GridDashboardLayout } from "../widgets/components/grid/GridDashboardLayout"
import { WidgetFillerContent } from "../widgets/components/WidgetFillerContent"
import {
  DashboardFiltersProvider,
  useDashboardFilters,
} from "../widgets/contexts/DashboardFiltersContext"
import { useDashboardWidgets } from "../widgets/hooks/useDashboardWidgets"
import type { ChartClickData } from "../widgets/types/chart"
import type { BucketClickData, DrillDownRunRecord } from "../widgets/types/core"
import { DEFAULT_DASHBOARD_DATE_PRESET } from "../widgets/types/dashboardConfig"
import {
  buildDashboardWidgetConfigurationHref,
  getDashboardDisplayName,
  resolveDashboardDateRangeOverride,
} from "../widgets/utils/dashboardPageState"
import { DashboardLoadingState } from "./DashboardLoadingState"
import { RunDetailsPanel } from "./RunDetailsPanel"
import { UnifiedMetricDrillDownPanel } from "./UnifiedMetricDrillDownPanel"

interface DashboardContentProps {
  dashboardId?: string
}

interface DashboardPageProps {
  dashboardId?: string
}

/**
 * Formats category value for display above RunDetailsTable
 * Converts raw category values (e.g., "0", "1") to user-friendly names (e.g., "NO", "YES")
 */
const formatCategoryForDisplay = (category: string | undefined): string => {
  if (!category) {
    return ""
  }

  // Check if this looks like a binary value (0 or 1)
  if (category === "0" || category === "1") {
    return formatBinaryValue(category)
  }

  // For other categories, return as-is (already user-friendly)
  return category
}

const DashboardContent: React.FC<DashboardContentProps> = ({ dashboardId }) => {
  const router = useRouter()
  const organizationSlug = router.query.slug as string
  const { trackEvent } = useAnalytics()
  const queryParams = useQueryParams()
  const urlStart = queryParams.get("start")
  const urlEnd = queryParams.get("end")
  const currentTab = queryParams.get("tab")

  // Using dashboard filters context
  const { state: filterState, handlerSetGlobalOverride } = useDashboardFilters()

  const {
    dashboards,
    currentDashboardId,
    widgets,
    isSavingDashboard,
    isLoading,
    handleRemoveWidget,
    handleCloneWidget,
    handleUpdateWidget,
    handleClearDashboard,
    handleExportDashboard,
    handleImportDashboard,
    handleRenameDashboard,
    handleDeleteDashboard,
    handleCreateDashboard,
    handleWidgetMove,
    handleWidgetWidthChange,
  } = useDashboardWidgets(dashboardId)

  const { savedPreferences } = useDashboardDatePreferences(currentDashboardId)

  const {
    selectedPointState,
    setSelectedPointState,
    handlePointClick,
    getSelectedPointForWidget,
    clearSelection,
  } = usePointSelection(widgets)

  // Remove modal disclosure since we're using dropdown navigation
  const { setCustomPageHeader } = usePageHeaderController()

  const currentDashboard = dashboards.find((d) => d.id === currentDashboardId)
  const activeDateRange = filterState.isOverrideActive
    ? filterState.globalDateRangeOverride
    : null

  const {
    unifiedMetricDrillDown,
    parentUnifiedMetricDrillDown,
    openUnifiedMetricDrillDown,
    openParentUnifiedMetricDrillDown,
    closeUnifiedMetricDrillDown,
  } = useUnifiedMetricDrillDownNavigation()

  const handleOpenDrillDownConversation = useCallback(
    (row: Pick<DrillDownRunRecord, "run_id" | "simulation_output_id">) => {
      queryParams.push(buildDrillDownConversationPath(row), organizationSlug)
    },
    [organizationSlug, queryParams],
  )

  const handleWidgetPointClick = useCallback(
    (point: BucketClickData, widgetId: string, metricId?: string) => {
      const widget = widgets.find((w) => w.id === widgetId)

      // If it's a chart widget, use the new drill-down panel
      if (widget?.type === "chart") {
        openUnifiedMetricDrillDown(
          point as unknown as ChartClickData,
          widgetId,
          metricId,
        )
      } else {
        // Use the old point selection for legacy widgets
        handlePointClick(point, widgetId, metricId)
      }
    },
    [widgets, handlePointClick, openUnifiedMetricDrillDown],
  )

  const handleWidgetChartClick = useCallback(
    (point: BucketClickData, widgetId: string, metricId?: string) => {
      const widget = widgets.find((w) => w.id === widgetId)
      if (widget?.type !== "chart") {
        return
      }

      setSelectedPointState(null)
      openUnifiedMetricDrillDown(
        point as unknown as ChartClickData,
        widgetId,
        metricId,
      )
    },
    [openUnifiedMetricDrillDown, setSelectedPointState, widgets],
  )

  const clearAllSelections = useCallback(() => {
    clearSelection()
    closeUnifiedMetricDrillDown()
  }, [clearSelection, closeUnifiedMetricDrillDown])

  const hasInitializedDateRef = React.useRef<string | null>(null)
  const lastDashboardIdRef = React.useRef<string | null>(null)
  const initialDateRangeOverride = useMemo(
    () =>
      resolveDashboardDateRangeOverride({
        defaultPreset: DEFAULT_DASHBOARD_DATE_PRESET,
        savedPreferences,
        urlStart,
        urlEnd,
      }),
    [savedPreferences, urlStart, urlEnd],
  )

  useEffect(() => {
    if (!currentDashboardId) {
      return
    }

    // Reset initialization when switching dashboards
    if (lastDashboardIdRef.current !== currentDashboardId) {
      lastDashboardIdRef.current = currentDashboardId
      hasInitializedDateRef.current = null
    }

    // Only initialize once per dashboard
    if (hasInitializedDateRef.current === currentDashboardId) {
      return
    }

    // Wait for dashboard to load (so we know if there are saved preferences)
    if (isLoading || !currentDashboard) {
      return
    }

    handlerSetGlobalOverride(initialDateRangeOverride)
    hasInitializedDateRef.current = currentDashboardId
  }, [
    currentDashboardId,
    currentDashboard,
    handlerSetGlobalOverride,
    initialDateRangeOverride,
    isLoading,
  ])

  // Track dashboard page loaded
  useEffect(() => {
    if (!isLoading && currentDashboardId) {
      trackEvent(TRACKING_EVENTS.DASHBOARD.PAGE_LOADED, {
        dashboard_id: currentDashboardId,
        dashboard_name: currentDashboard?.display_name || "",
        widget_count: widgets.length,
      })
    }
  }, [
    isLoading,
    currentDashboardId,
    currentDashboard?.display_name,
    widgets.length,
    trackEvent,
  ])

  useEscapeShortcut({
    description: "Back to Dashboard",
    enabled: !!selectedPointState || !!unifiedMetricDrillDown,
    handler: clearAllSelections,
    priority: ESCAPE_SHORTCUT_PRIORITY.PAGE_FALLBACK,
  })

  // Compute focused widget title for breadcrumb
  const focusedWidgetForBreadcrumb = useMemo(() => {
    const widgetId =
      selectedPointState?.widgetId ?? unifiedMetricDrillDown?.widgetId
    if (!widgetId) {
      return null
    }
    return widgets.find((w) => w.id === widgetId) ?? null
  }, [selectedPointState, unifiedMetricDrillDown, widgets])

  const focusedChartTitle =
    focusedWidgetForBreadcrumb?.config?.title ||
    focusedWidgetForBreadcrumb?.title ||
    null

  const dashboardDisplayName = getDashboardDisplayName(currentDashboard)
  const currentDrillDownLabel = useMemo(
    () =>
      unifiedMetricDrillDown
        ? getDrillDownBreadcrumbLabel(unifiedMetricDrillDown.clickData, {
            chartTitle: focusedChartTitle,
          })
        : null,
    [focusedChartTitle, unifiedMetricDrillDown],
  )
  const hasPointDrillDown =
    !!unifiedMetricDrillDown &&
    (unifiedMetricDrillDown.clickData.drillDownScope ?? "point") === "point"
  const handleFocusedChartBreadcrumbClick =
    parentUnifiedMetricDrillDown && hasPointDrillDown
      ? openParentUnifiedMetricDrillDown
      : clearAllSelections
  const hasSelectedPoint = !!selectedPointState
  const {
    dashboardInsightChart,
    dashboardInsightRows,
    focusedInsightSimulationOutputId,
    handleInsightEvidenceLinkClick,
    handleInsightEvidenceLinkHover,
    highlightedInsightSimulationOutputId,
    unifiedDrillDownRowsState,
  } = useUnifiedMetricDrillDownInsights({
    currentDashboardId,
    focusedChartTitle,
    onInsightRowOpen: handleOpenDrillDownConversation,
    unifiedMetricDrillDown,
  })

  const handleBreadcrumbKeyDown = useCallback(
    (event: React.KeyboardEvent, action: () => void) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return
      }

      event.preventDefault()
      action()
    },
    [],
  )

  // Update breadcrumb to include chart title when in focused mode
  useEffect(() => {
    if (focusedChartTitle && dashboardDisplayName) {
      setCustomPageHeader(
        <div className="flex flex-1 min-w-0 items-center">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <span>Dashboard</span>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={clearAllSelections}
                  tabIndex={0}
                  onKeyDown={(event) =>
                    handleBreadcrumbKeyDown(event, clearAllSelections)
                  }
                  className="cursor-pointer"
                  role="button"
                >
                  {dashboardDisplayName}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {hasPointDrillDown || hasSelectedPoint ? (
                  <BreadcrumbLink
                    onClick={handleFocusedChartBreadcrumbClick}
                    tabIndex={0}
                    onKeyDown={(event) =>
                      handleBreadcrumbKeyDown(
                        event,
                        handleFocusedChartBreadcrumbClick,
                      )
                    }
                    className="cursor-pointer"
                    role="button"
                  >
                    {focusedChartTitle}
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{focusedChartTitle}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
              {currentDrillDownLabel && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{currentDrillDownLabel}</BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
          </Breadcrumb>
        </div>,
      )
    } else {
      setCustomPageHeader(null)
    }

    return () => {
      setCustomPageHeader(null)
    }
  }, [
    focusedChartTitle,
    dashboardDisplayName,
    currentDrillDownLabel,
    hasPointDrillDown,
    handleFocusedChartBreadcrumbClick,
    setCustomPageHeader,
    clearAllSelections,
    hasSelectedPoint,
    handleBreadcrumbKeyDown,
  ])

  const getWidgetConfigurationHref = useCallback(
    (widgetId?: string) =>
      queryParams.getHref(
        buildDashboardWidgetConfigurationHref({
          organizationSlug,
          dashboardId: currentDashboardId ?? "",
          ...(widgetId ? { widgetId } : {}),
          currentTab,
          activeDateRange,
        }),
        organizationSlug,
      ),
    [
      activeDateRange,
      currentDashboardId,
      currentTab,
      organizationSlug,
      queryParams,
    ],
  )

  const handleAddWidgetClick = useCallback(() => {
    if (!currentDashboardId) {
      return
    }

    void router.push(getWidgetConfigurationHref())
  }, [currentDashboardId, getWidgetConfigurationHref, router])

  const handleConfigureWidget = useCallback(
    (id: string) => {
      if (!currentDashboardId || !widgets.find((w) => w.id === id)) {
        return
      }

      void router.push(getWidgetConfigurationHref(id))
    },
    [currentDashboardId, getWidgetConfigurationHref, router, widgets],
  )

  // While the dashboard and its widget list load, the page shows its own chrome
  // and nothing else. The widgets themselves are the only placeholder: each card
  // arrives at its final size on the grid and fills in as its data lands, so a
  // stand-in layout here would only be a second arrangement to replace.
  if (isLoading) {
    return <DashboardLoadingState />
  }

  // Zero dashboards: show empty state and do not render children that require a valid dashboard id
  if (!currentDashboardId) {
    return (
      <LogInRequired>
        <div className="container mx-auto max-w-7xl px-6 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <p className="text-coval-text-muted mb-4">No dashboards yet</p>
              <Button
                color="primary"
                data-testid="dashboard-create-default-button"
                onPress={() => {
                  void handleCreateDashboard("Default Dashboard")
                }}
              >
                Create default dashboard
              </Button>
            </div>
          </div>
        </div>
      </LogInRequired>
    )
  }

  const isFocusedMode = !!selectedPointState || !!unifiedMetricDrillDown
  const focusedWidget = focusedWidgetForBreadcrumb

  return (
    <LogInRequired>
      {/* Declares the dashboard this page shows. Read by
          app-covi/utils/viewContext — data-only, no module import. */}
      <div
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        data-testid="dashboard-page"
        data-covi-view={
          currentDashboardId
            ? JSON.stringify({
                kind: "dashboard",
                id: currentDashboardId,
                name: currentDashboard?.display_name,
              })
            : undefined
        }
      >
        {/* Static Header - not in scroll container so it never moves */}
        {/* Hide entirely when in unified drill-down mode (no content to show) */}
        {!(isFocusedMode && !selectedPointState) && (
          <div className="z-10 min-w-0 shrink-0">
            <div className="flex min-w-0 items-center justify-between px-6 pb-4">
              <div className="flex min-w-0 items-center gap-2">
                {/* Calendar button + Dashboard Actions (currentDashboardId is defined here) */}
                {!isFocusedMode && (
                  <>
                    <CalendarDateRangeButton
                      currentDashboardId={currentDashboardId}
                    />
                    <DashboardActions
                      dashboardId={currentDashboardId}
                      dashboardUrl={queryParams.getHref(
                        `/dashboard/${currentDashboardId}`,
                        organizationSlug,
                      )}
                      createdAt={currentDashboard?.created_at}
                      updatedAt={currentDashboard?.last_updated_at}
                      onClearDashboard={handleClearDashboard}
                      onDeleteDashboard={() =>
                        handleDeleteDashboard(currentDashboardId)
                      }
                      onExportDashboard={handleExportDashboard}
                      onImportDashboard={handleImportDashboard}
                      onRenameDashboard={
                        currentDashboard?.is_default
                          ? undefined
                          : (newName) =>
                              handleRenameDashboard(currentDashboardId, newName)
                      }
                      widgetCount={widgets.length}
                      isDefaultDashboard={currentDashboard?.is_default ?? false}
                      dashboardName={
                        currentDashboard?.display_name ?? "Dashboard"
                      }
                    />
                    {isSavingDashboard && (
                      <span
                        className="text-xs text-coval-text-muted"
                        aria-live="polite"
                      >
                        Saving changes...
                      </span>
                    )}
                    {/* <DashboardFiltersDropdown
                    organizationSlug={organizationSlug}
                    currentDashboardId={currentDashboardId}
                  /> */}
                  </>
                )}

                {/* Widget Title removed from header — now inside chart widget */}
              </div>

              <div className="flex items-center gap-1.5">
                {/* Run count and category info - right aligned when in focused mode */}
                {isFocusedMode && selectedPointState && (
                  <div className="flex items-center gap-3 mr-4">
                    {selectedPointState.point.category && (
                      <span
                        className="text-2xl font-medium"
                        style={{
                          color:
                            selectedPointState.point.color ||
                            "var(--coval-warning-text)",
                        }}
                      >
                        {formatCategoryForDisplay(
                          selectedPointState.point.category,
                        )}
                      </span>
                    )}
                    <span className="text-coval-text-muted">
                      {selectedPointState.point.bucketCount} Results
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Content - only this area scrolls; gray bg fills viewport */}
        <div
          className={`min-h-0 min-w-0 flex-1 transition-all duration-500 ease-in-out ${isFocusedMode ? "overflow-x-auto overflow-y-hidden" : "overflow-x-hidden overflow-y-auto"} bg-surface-contrast`}
        >
          {widgets.length === 0 ? (
            <div className="px-8 pb-6 pt-6">
              <EmptyDashboardState onAddWidget={handleAddWidgetClick} />
            </div>
          ) : isFocusedMode && focusedWidget ? (
            /* Focused Layout - 50/50 flexbox design */
            <div className="flex h-full min-w-0 flex-row gap-6 pl-6 animate-in fade-in duration-500">
              {/* Chart Section - 50% width */}
              <div className="flex min-h-0 min-w-[400px] flex-1 flex-col gap-4 overflow-hidden py-3 pr-1">
                <Card className="h-[350px] shrink-0">
                  <div className="flex h-full flex-col p-6">
                    <h3 className="text-sm font-semibold text-coval-text truncate mb-2 shrink-0">
                      {focusedWidget.config?.title ||
                        focusedWidget.title ||
                        focusedWidget.type}
                    </h3>
                    <div className="flex-1 min-h-0">
                      <WidgetFillerContent
                        type={focusedWidget.type}
                        widget={focusedWidget}
                        onChartClick={handleWidgetChartClick}
                        onPointClick={handleWidgetPointClick}
                        selectedPointInChart={getSelectedPointForWidget(
                          focusedWidget.id,
                        )}
                      />
                    </div>
                  </div>
                </Card>
                <div className="min-h-0 flex-1 pb-3">
                  {unifiedMetricDrillDown ? (
                    <DashboardDrilldownInsightsPanel
                      chart={dashboardInsightChart}
                      rows={dashboardInsightRows}
                      isRowsLoading={unifiedDrillDownRowsState.isLoading}
                      rowsError={unifiedDrillDownRowsState.error}
                      onEvidenceLinkClick={handleInsightEvidenceLinkClick}
                      onEvidenceLinkHover={handleInsightEvidenceLinkHover}
                    />
                  ) : null}
                </div>
              </div>

              {/* Run Details Panel - 50% width */}
              <div className="flex-1 min-w-[300px] h-full">
                <div className="h-full">
                  {unifiedMetricDrillDown ? (
                    <UnifiedMetricDrillDownPanel
                      clickData={unifiedMetricDrillDown.clickData}
                      baselineId={focusedWidget?.config?.baselineId}
                      drillDownRowsState={unifiedDrillDownRowsState}
                      focusedSimulationOutputId={
                        focusedInsightSimulationOutputId
                      }
                      highlightedSimulationOutputId={
                        highlightedInsightSimulationOutputId
                      }
                      onClose={closeUnifiedMetricDrillDown}
                    />
                  ) : selectedPointState ? (
                    <RunDetailsPanel
                      selectedPoint={selectedPointState.point}
                      metricId={selectedPointState.metricId}
                      isMonitoring={selectedPointState.isMonitoring}
                      dataSource={selectedPointState.dataSource}
                      widgetConfig={focusedWidget?.config}
                      onClose={() => setSelectedPointState(null)}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            /* Regular Grid Layout */
            <GridDashboardLayout
              widgets={widgets}
              dashboardId={currentDashboardId}
              onRemove={handleRemoveWidget}
              onConfigure={handleConfigureWidget}
              onClone={(widget) => {
                handleCloneWidget(widget)
              }}
              onConfigChange={(widgetId, config) => {
                void handleUpdateWidget(widgetId, config)
              }}
              onMoveWidget={handleWidgetMove}
              onWidthChange={handleWidgetWidthChange}
              onChartClick={handleWidgetChartClick}
              onPointClick={handleWidgetPointClick}
              getSelectedPointForWidget={getSelectedPointForWidget}
            />
          )}
        </div>
      </div>

      {/* No modals needed anymore - using direct navigation */}
    </LogInRequired>
  )
}

export const DashboardPage: React.FC<DashboardPageProps> = ({
  dashboardId,
}) => {
  return (
    <DashboardFiltersProvider>
      <DashboardContent key={dashboardId} dashboardId={dashboardId} />
    </DashboardFiltersProvider>
  )
}
